import "./applications.scss";
import { type FC, useMemo } from "react";
import { type ShopfrontHighlighter, useSyntaxHighlighter } from "@onshopfront/components";
import FontAwesomeLight from "../../../app/Utilities/Platform/FontAwesome/FontAwesomeLight";
import type { ApplicationRequestLogType } from "./ApplicationHistory";

function formatBody(body: string | null, highlighter: ShopfrontHighlighter | null): string {
    if(!body || !highlighter) {
        return body ?? "";
    }

    try {
        const prettyJSON = JSON.stringify(JSON.parse(body), null, 2);

        return highlighter.codeToHtml(prettyJSON, {
            lang : "json",
            theme: "github-dark-dimmed",
        });
    } catch {
        // Do nothing, just display the raw data
        return body ?? "";
    }
}

const ApplicationCallDetails: FC<{
    log: ApplicationRequestLogType;
}> = ({ log }) => {
    const { method, status, timestamp, user_agent, vendor, request_body, response_body } = log;
    const syntaxHighlighter = useSyntaxHighlighter();

    const isSuccess = (200 <= status && status <= 299 && response_body !== "Response contained a body");

    const requestFormatted = useMemo(() => {
        return formatBody(request_body, syntaxHighlighter);
    }, [ request_body, syntaxHighlighter ]);

    const responseFormatted = useMemo(() => {
        return formatBody(response_body, syntaxHighlighter);
    }, [ response_body, syntaxHighlighter ]);

    return (
        <div className="call-details">
            <div className="call-details-header">
                <div>
                    <b>Method:</b>
                    {method}
                </div>
                <div>
                    <b>Status:</b>
                    {status}
                    <span className={(isSuccess ? "status-success" : "status-error")}>
                        <FontAwesomeLight icon={(isSuccess ? "check-circle" : "times-circle")} />
                    </span>
                </div>
                <div><b>Vendor:</b>{vendor}</div>
                <div><b>Date:</b>{timestamp} UTC</div>
                <div><b>User Agent:</b>{user_agent}</div>
            </div>
            <div className="call-details-request">
                <h3>Request</h3>
                <pre className="language-http">
                    <code dangerouslySetInnerHTML={{ __html: requestFormatted }} />
                </pre>
            </div>
            <div className="call-details-response">
                <h3>Response</h3>
                <pre className="language-json">
                    <code dangerouslySetInnerHTML={{ __html: responseFormatted }} />
                </pre>
            </div>
        </div>
    );
};

export default ApplicationCallDetails;
